import React from 'react'
import { makeObservable, observable, action } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import * as R from 'ramda'
import Anchor from '../../../../UniformComponents/Anchor'

class DataViewerStore {
    filterOperators = [
        {
            value: 'eq',
            label: 'Equal',
        },
        {
            value: 'lt',
            label: 'Less than',
        },
        {
            value: 'lte',
            label: 'Less than or equal',
        },
        {
            value: 'gte',
            label: 'Greater than',
        },
        {
            value: 'gt',
            label: 'Greater than or equal',
        },
        {
            value: 'neq',
            label: 'Not equal',
        },
        {
            value: 'in',
            label: 'In',
        },
        {
            value: 'not_in',
            label: 'Not in',
        },
        {
            value: 'isnull',
            label: 'Is null',
        },
        {
            value: 'notnull',
            label: 'Not null',
        },
    ]

    @observable filters = []

    @observable showFilters = false

    @observable schemas = []

    selectedSchema = 'explori_v3'

    @observable tables = []

    @observable loading = true

    selectedTable = ''

    primaryKeys = {}

    @observable dataTableKey = uuidv4()

    @observable showData = false

    @observable showDownloadModal = false

    @observable dataSet = null

    constructor() {
        makeObservable(this)
    }

    addFilter = () => {
        this.filters.push({
            id: uuidv4(),
            column: null,
            operator: 'eq',
            value: '',
        })
    }

    deleteFilter = (filterId) => {
        const filters = this.filters
            .slice()
            .filter(({ id }) => id !== filterId)

        this.filters = filters
    }

    fromQueryParams = () => {
        const queryParams = new URLSearchParams(window.location.search)

        this.selectedSchema = queryParams.get('schema') || this.selectedSchema
        this.selectedTable = queryParams.get('tablename') || this.selectedTable
    }

    filterFromQueryParams = () => {
        const queryParams = new URLSearchParams(window.location.search)
        this.filters = Array.from(queryParams.entries())
            .filter(([key]) => ['filter'].includes(key))
            .map(([_, filter]) => {
                const [column, operator, value] = filter.split('|')
                return ({
                    id: uuidv4(),
                    column,
                    operator,
                    value,
                })
            })

        this.showFilters = this.showFilters || this.filters.length > 0
    }

    init = async (store) => {
        this.store = store

        this.fromQueryParams()

        const result = await store.http.get(`/dataviewer?schema=${this.selectedSchema}`)

        this.schemas = result.schemas.map((schema) => ({
            label: schema,
            value: schema,
        }))

        this.tables = result.tables.map((table) => ({
            value: table,
            label: table,
        }))

        this.primaryKeys = result.primaryKeys.reduce((primaryKeys, { table, column }) => {
            if (!Object.hasOwnProperty.call(primaryKeys, column)) {
                primaryKeys[column.toLowerCase()] = table
            }
            return primaryKeys
        }, {})

        if (this.selectedTable) {
            await this.changeTable({ value: this.selectedTable })
            this.filterFromQueryParams()
            this.viewResults()
        }
        this.loading = false
    }

    toQueryString = (queryParams) => {
        const query = []
        queryParams.forEach((value, key) => {
            query.push(`${key}=${value}`)
        })

        return query.join('&')
    }

    setQueryParams = () => {
        const queryParams = new URLSearchParams()
        queryParams.delete('filter')
        queryParams.set('schema', this.selectedSchema)
        queryParams.set('tablename', this.selectedTable)
        this.filters.forEach(({ column, operator, value }) => {
            queryParams.append('filter', `${column}|${operator}|${value}`)
        })

        if (!this.loading) {
            history.replaceState(null, null, `?${this.toQueryString(queryParams)}`)
        }
    }

    viewResults = () => {
        this.setQueryParams()
        this.dataTableKey = uuidv4()
        this.showData = true
    }

    hidedownloadModal = () => {
        this.downloadUrl = undefined
        this.showDownloadModal = false
    }

    download = async () => {
        const { store } = this
        const { selectedTable, selectedSchema, origCols } = this

        try {
            const { url } = await store.http.post('/dataviewer/download', JSON.stringify({
                table: selectedTable,
                schema: selectedSchema,
                columns:
                origCols,
                filters: this.getFilters(),
            }))
            this.downloadUrl = url
            this.showDownloadModal = true
        } catch (e) {
            console.error(e)
            store.addNotification(null, 'Error downloading data', 'error')
        }
    }

    getFilters = () => (
        this.filters
            .filter(({ column }) => !R.isNil(column) && !R.isEmpty(column))
            .map((filter) => {
                if (['in', 'not_in'].includes(filter.operator) && typeof filter.value === 'string') {
                    console.log('nope', filter.operator, filter.value)
                    filter.value = filter.value
                        .split(',')
                        .map((value) => value.trim())
                        .filter((value) => !R.isNil(value) && !R.isEmpty(value))
                }
                return filter
            })
    )

    changeSchema = async ({ value, name }) => {
        const { store } = this
        const result = await store.http.get(`/dataviewer/schema/${value}`)
        this.selectedSchema = value

        this.tables = result.tables.map((table) => ({
            value: table,
            label: table,
        }))

        this.primaryKeys = result.primaryKeys.reduce((primaryKeys, { table, column }) => {
            if (!Object.hasOwnProperty.call(primaryKeys, column)) {
                primaryKeys[column.toLowerCase()] = table
            }
            return primaryKeys
        }, {})

        this.filters = []
        this.showFilters = false
        this.selectedTable = undefined
        this.setQueryParams()
        this.showData = false
    }

    changeTable = async ({ value, name }) => {
        const { store } = this

        const result = await store.http.post('/dataviewer/table', JSON.stringify({ table: value, schema: this.selectedSchema }))

        const cols = result.map((col) => ({
            value: col.label,
            label: col.name,
            name: col.label,
            key: col.name,
            isBinary: col.isBinary,
            isDateTime: col.isDateTime,
            isPrimary: col.isPrimary,
            isJSON: col.isJSON,
        }))

        this.selectedTable = value
        this.origCols = result
        this.cols = cols
        this.filterColumns = cols.map(({ label, name }) => ({
            label,
            value: name,
        }))

        this.filters = []
        this.showFilters = true
        this.setQueryParams()
        this.showData = false
    }

    @action
    getData = async (from, to) => {
        const { store } = this
        const { selectedTable, selectedSchema, origCols } = this
        // this endpoint behaves as if `to` is `pageSize`,
        // so {from: 20, to: 20} returns 20 records after record #20
        const range = to - from
        try {
            const { records, numRows } = await store.http.post('/dataviewer/data', JSON.stringify({
                table: selectedTable,
                schema: selectedSchema,
                columns: origCols,
                filters: this.getFilters(),
                from,
                to: range,
            }))
            this.recordCount = numRows
            this.dataSet = records
            return { rows: records, numRows }
        } catch (error) {
            console.error('Error getting data for dataViewer: ', error)
            return { rows: [], numRows: 0 }
        }
    }

    generateTableColumns = (columns) => {
        const { selectedSchema, store } = this
        console.log('generateTableColumns', this.primaryKeys)
        return columns.map((column) => {
            const tableColumn = {
                name: column.name,
                key: column.name,
            }
            // if (column.isDateTime) {
            //     tableColumn.dataTransform = (value) => (
            //         (R.isNil(value) || R.isEmpty(value))
            //             ? ''
            //             : moment(value).format('YYYY-MM-DD HH:mm:ss')
            //     )
            // } else
            if (R.hasIn(column.name.toLowerCase(), this.primaryKeys)) {
                const table = this.primaryKeys[column.name.toLowerCase()]
                tableColumn.dataTransform = (value) => (
                    (R.isNil(value) || R.isEmpty(value))
                        ? ''
                        : (
                            <div>
                                {value}
                                {' '}
                                <Anchor
                                    isExternal
                                    href={store.getDataviewerLink({
                                        schema: selectedSchema,
                                        table,
                                        column: column.name,
                                        operator: 'eq',
                                        value,
                                    })}
                                    target="_blank"
                                >
                                    <i className="fas fa-external-link-square-alt" />
                                </Anchor>
                            </div>
                        )
                )
            } else if (column.isJSON) {
                tableColumn.dataTransform = (value) => (
                    JSON.stringify(value)
                )
            }

            return tableColumn
        })
    }
}

export default DataViewerStore
